<template>
    <div>
        <c-loader :loader="loader" />
        <b-form @submit.prevent="onSubmit" @reset.prevent="onReset" novalidate v-show="!loader">
            <b-row>
                <b-col>
                    <c-title
                        id="btn-target-title"
                        :state="checkState('title')"
                        :value.sync="$v.form.title.$model"
                        :disabled="!isAllowedToCreateOrEdit"
                        :label="
                            isIndicator()
                                ? `${$t('form.label.whatIsTheIndicator')}*`
                                : `${$t('form.label.whatIsTheGoal')}*`
                        "
                        :placeholder="
                            isIndicator()
                                ? $t('form.placeholder.enterYourIndicator')
                                : $t('form.placeholder.enterYourGoal')
                        "
                    >
                        <span v-if="!$v.form.title.required">{{ $t('form.validations.required') }}</span>
                    </c-title>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <c-description
                        :state="checkState('description')"
                        :value.sync="$v.form.description.$model"
                        :disabled="!isAllowedToCreateOrEdit"
                        :label="$t('form.label.description')"
                        :placeholder="$t('form.placeholder.targetDescription')"
                    />
                </b-col>
            </b-row>
            <b-row class="mb-4">
                <b-col md="6" xl="3">
                    <c-target
                        :state="checkState('target')"
                        :value.sync="$v.form.target.$model"
                        :disabled="!isAllowedToCreateOrEdit || isEnableTarget"
                        :label="isTargetRequired ? `${$t('form.label.target')}*` : $t('form.label.target')"
                        :placeholder="$t('form.placeholder.target')"
                    >
                        <span v-if="!$v.form.target.required">{{ $t('form.validations.required') }}</span>
                        <span v-if="!$v.form.target.decimal">{{ $t('form.validations.decimal') }}</span>
                    </c-target>
                </b-col>
                <b-col md="6" xl="3">
                    <c-unit-of-measurement
                        :state="checkState('unitOfMeasurement')"
                        :value.sync="$v.form.unitOfMeasurement.$model"
                        @ready="isUnitOfMeasurementReady = $event"
                        :disabled="!isAllowedToCreateOrEdit"
                        :label="$t('form.label.unitOfMeasurement')"
                    />
                </b-col>
                <b-col md="6" xl="3">
                    <c-weight
                        :state="checkState('weight')"
                        :value.sync="$v.form.weight.$model"
                        :disabled="(!isAllowedToCreateOrEdit && !isReferencedGoal) || isIndicator()"
                        :label="$t('form.label.weight')"
                        :placeholder="$t('form.placeholder.weight')"
                    >
                        <span v-if="!$v.form.weight.decimal">{{ $t('form.validations.decimal') }}</span>
                        <span v-if="!$v.form.weight.decimalWeight">{{ $t('form.validations.decimalWeight') }}</span>
                    </c-weight>
                </b-col>
                <b-col md="6" xl="3">
                    <c-active-period
                        :state="checkState('activePeriod')"
                        :value.sync="$v.form.activePeriod.$model"
                        @ready="isActivePeriodReady = $event"
                        :disabled="!isAllowedToCreateOrEdit"
                        :label="`${$t('form.label.activePeriod')}*`"
                    >
                        <span v-if="!$v.form.activePeriod.required">{{ $t('form.validations.required') }}</span>
                    </c-active-period>
                </b-col>
            </b-row>

            <b-card v-if="isRangesOfPerformanceEnableForChange" :header="$t('ranges.ranges')">
                <b-row v-if="isRangesOfPerformance">
                    <b-col
                        md="6"
                        xl="3"
                        :key="range.id.$model"
                        v-for="(range, index) in $v.form.rangesOfPerformance.$each.$iter"
                    >
                        <b-form-group :label="range.title.$model">
                            <c-range-of-performance-value
                                :state="checkArrayState(range, 'value')"
                                :value.sync="range.value.$model"
                                :disabled="!isAllowedToCreateOrEdit"
                                :placeholder="$t('form.placeholder.range')"
                                :range="range"
                                :index="index"
                            >
                                <span v-if="!range.value.decimal">{{ $t('form.validations.decimal') }}</span>
                            </c-range-of-performance-value>
                            <c-range-of-performance-description
                                :state="checkArrayState(range, 'description')"
                                :value.sync="range.description.$model"
                                :disabled="!isAllowedToCreateOrEdit"
                                :placeholder="$t('form.placeholder.rangeDescription')"
                                :range="range"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <p class="mb-0 text-center font-weight-bold font-italic" v-else>
                    {{ $t('ranges.emptyRanges') }}
                </p>
            </b-card>

            <b-collapse id="collapse-advanced-options" v-model="toggleAdvancedOptions">
                <b-card :header="$t('form.button.advancedOptions')">
                    <b-row>
                        <b-col md="6" xl="3">
                            <c-owner
                                :state="checkState('owner')"
                                :value.sync="$v.form.owner.$model"
                                @ready="isOwnerReady = $event"
                                :disabled="!isAllowedToCreateOrEdit"
                                :isEditing="this.isEdit"
                                :label="`${$t('form.label.owner')}*`"
                                :can-assign="canAssign"
                                :moduleForm="moduleForm"
                                :defaultOwnerId="defaultOwnerId"
                            >
                                <span v-if="!$v.form.owner.required">{{ $t('form.validations.required') }}</span>
                            </c-owner>
                        </b-col>
                        <b-col md="6" xl="3">
                            <c-initial-value
                                :state="checkState('initialValue')"
                                :value.sync="$v.form.initialValue.$model"
                                :disabled="!isAllowedToCreateOrEdit"
                                :label="$t('form.label.initialValue')"
                                :placeholder="$t('form.placeholder.initialValue')"
                            >
                                <span v-if="!$v.form.initialValue.decimal">{{ $t('form.validations.decimal') }}</span>
                            </c-initial-value>
                        </b-col>
                        <b-col md="12" xl="6">
                            <c-year-to-date
                                :state="checkState('yearToDate')"
                                :value.sync="$v.form.yearToDate.$model"
                                @ready="isYearToDateReady = $event"
                                :disabled="!isAllowedToCreateOrEdit"
                                :label="`${$t('form.label.yearToDate')}*`"
                                :moduleForm="moduleForm"
                            >
                                <span v-if="!$v.form.yearToDate.required">{{ $t('form.validations.required') }}</span>
                            </c-year-to-date>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6" xl="3">
                            <c-level
                                :state="checkState('level')"
                                :value.sync="$v.form.level.$model"
                                :disabled="!isAllowedToCreateOrEdit"
                                :label="`${$t('form.label.level')}*`"
                                @change="onChangeLevel"
                                :isEditing="isEdit"
                                :allowedLevels="currentEventPermissions"
                                :isLibraryTarget="isLibraryTarget"
                            >
                                <span v-if="!$v.form.level.required">{{ $t('form.validations.required') }}</span>
                                <span v-if="!$v.form.level.groupLevel">{{ $t('form.validations.invalidOption') }}</span>
                            </c-level>
                        </b-col>
                        <b-col md="6" xl="3">
                            <c-sections
                                lazyLoading
                                :state="checkState('section')"
                                :value.sync="$v.form.section.$model"
                                :selected="$v.form.section.$model"
                                :enabledSectionType="getSelectedLevel"
                                :disabled="!isEnableSections || !isAllowedToCreateOrEdit"
                            >
                                <span v-if="!$v.form.section.required">{{ $t('form.validations.required') }}</span>
                            </c-sections>
                        </b-col>
                        <b-col md="6" xl="3">
                            <c-polarity
                                :state="checkState('polarity')"
                                :value.sync="$v.form.polarity.$model"
                                :selected="$v.form.polarity.$model"
                                :label="`${$t('form.label.polarity')}*`"
                                :disabled="!isAllowedToCreateOrEdit"
                            />
                        </b-col>
                    </b-row>
                </b-card>
            </b-collapse>

            <p class="font-italic text-muted">
                {{ $t('form.warning.requiredFields') }}
            </p>

            <div class="float-right">
                <b-button class="mr-1" variant="outline-info" v-b-toggle.collapse-advanced-options>{{
                    $t('form.button.advancedOptions')
                }}</b-button>
                <b-button class="mr-1" variant="secondary" :disabled="isSubmitting" @click="$router.back()">{{
                    $t('form.button.cancel')
                }}</b-button>
                <c-button-loader
                    type="submit"
                    variant="primary"
                    :loader="isSubmitting"
                    :disabled="(!isAllowedToCreateOrEdit && !isReferencedGoal) || isSubmitting"
                    >{{ $t('form.button.save') }}</c-button-loader
                >
            </div>
        </b-form>
    </div>
</template>

<script>
import i18n from '@/i18n'
import { cloneDeep } from 'lodash'
import { validationMixin } from 'vuelidate'
import validationOfFields from './validation'
import initialValuesOfFields from './initial-values'
import {
    Title as CTitle,
    Target as CTarget,
    UnitOfMeasurement as CUnitOfMeasurement,
    Weight as CWeight,
    ActivePeriod as CActivePeriod,
    Owner as COwner,
    InitialValue as CInitialValue,
    YearToDate as CYearToDate,
    Level as CLevel,
    Sections as CSections,
    Description as CDescription,
    Polarity as CPolarity,
    isEnableSections,
    getSelectedLevel,
    RangeOfPerformanceValue as CRangeOfPerformanceValue,
    RangeOfPerformanceDescription as CRangeOfPerformanceDescription,
    getRangesOfPerformanceFromApi,
    rangeOfPerformanceMapperApiToForm,
    rangeOfPerformanceMergeApiWithForm,
} from '@/views/components/fields'
import CLoader from '@/views/components/loader'
import CButtonLoader from '@/views/components/button-loader'
import { userPermissionsMixin } from '@/mixins'

export default {
    name: 'Form',
    components: {
        CTitle,
        CTarget,
        CUnitOfMeasurement,
        CWeight,
        CActivePeriod,
        COwner,
        CInitialValue,
        CYearToDate,
        CLevel,
        CSections,
        CDescription,
        CLoader,
        CButtonLoader,
        CRangeOfPerformanceValue,
        CRangeOfPerformanceDescription,
        CPolarity,
    },
    props: [
        'isEdit',
        'target',
        'objectiveId',
        'canEdit',
        'isReferencedGoal',
        'canCreate',
        'canCreateLibraryTarget',
        'currentEventPermissions',
        'moduleForm',
        'isLibraryTarget',
        'defaultOwnerId',
        'profileUserId',
    ],
    mixins: [validationMixin, userPermissionsMixin],
    validations: {
        form: { ...validationOfFields },
    },
    data() {
        return {
            // Ready
            isActivePeriodReady: false,
            isUnitOfMeasurementReady: false,
            isOwnerReady: false,
            isYearToDateReady: false,
            isRangesOfPerformanceReady: false,
            // Behavior of fields
            isSubmitting: false,
            isRangesOfPerformance: false,
            toggleAdvancedOptions: false,
            // Initial values of fields
            form: { ...initialValuesOfFields, type: this.moduleForm },
        }
    },
    mounted() {
        getRangesOfPerformanceFromApi()
            .then((resp) => {
                const rangesToForm = rangeOfPerformanceMapperApiToForm(resp)
                this.form.rangesOfPerformance = cloneDeep(rangesToForm)
                this.isRangesOfPerformance = rangesToForm.length
            })
            .finally(() => {
                this.isRangesOfPerformanceReady = true
            })
    },
    computed: {
        loader() {
            return !(
                this.isActivePeriodReady &&
                this.isUnitOfMeasurementReady &&
                this.isOwnerReady &&
                this.isYearToDateReady &&
                this.isRangesOfPerformanceReady
            )
        },
        isTargetRequired() {
            return this.isIndicator() || (this.$v.form.yearToDate.$model && this.$v.form.yearToDate.$model.isCalculated)
        },
        isEnableSections() {
            return isEnableSections(this.form.level, 'personal')
        },
        getSelectedLevel() {
            return getSelectedLevel(this.form.level, 'personal')
        },
        isEnableTarget() {
            if (this.isIndicator()) {
                return false
            }
            return this.yearToDateIsManual
        },
        yearToDateIsManual() {
            return this.form.yearToDate && this.form.yearToDate.label == i18n.t('yearToDate.manual')
        },
        isAllowedToCreateOrEdit() {
            if (this.isReferencedGoal) {
                return false
            }

            if (this.isEdit) {
                return this.canEdit
            }
            if (this.isLibraryTarget) {
                return this.canCreateLibraryTarget
            }
            return this.canCreate
        },
        canAssign() {
            return this.form.level ? this.currentEventPermissions[this.form.level.value].assign : false
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        userHasRolePermission() {
            const availableModules = []
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_target_master']
            return this.userHasPermission(this.currentUser, availableRoles, availableModules)
        },
        isRangesOfPerformanceEnableForChange() {
            return this.userHasRolePermission ? true : this.currentUser.company.allow_range_edition
        },
    },
    methods: {
        onChangeLevel() {
            this.form.section = null
            this.$nextTick(() => {
                this.$v.form.section.$reset()
            })
        },
        onSubmit() {
            if (this.validate()) {
                this.$nextTick(() => {
                    this.$emit(
                        'onSubmit',
                        {
                            ...cloneDeep(this.form),
                            ...{ checkInFrequency: 'monthly' },
                            ...{ objectiveId: this.objectiveId },
                            ...{ targetLibrary: this.isLibraryTarget },
                            ...{ profileUserId: this.profileUserId },
                        },
                        this.onReset,
                        this.isLoading
                    )
                })
            }
        },
        isLoading(state) {
            this.isSubmitting = state
        },
        isIndicator() {
            return this.moduleForm == 'indicator'
        },
        onReset() {
            this.$nextTick(() => {
                this.$v.$reset()
            })
        },
        checkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        checkArrayState(current, key) {
            return current[key].$dirty ? !current[key].$invalid : null
        },
        validate() {
            this.$v.$touch()
            return !this.$v.$invalid
        },
        touchFields(...fields) {
            fields.forEach((key) => {
                if (this.$v.form[key].$model) {
                    this.$v.form[key].$touch()
                }
            })
        },
    },
    watch: {
        loader(isLoading) {
            if (!isLoading && this.isEdit) {
                this.target.then((target) => {
                    const { rangesOfPerformance } = target
                    const updatedRangesOfPerformance = rangeOfPerformanceMergeApiWithForm(
                        rangesOfPerformance,
                        this.form.rangesOfPerformance
                    )
                    target.rangesOfPerformance = updatedRangesOfPerformance

                    this.form = target

                    this.touchFields('unitOfMeasurement', 'owner', 'level', 'section', 'target')
                })
            }
        },
        yearToDateIsManual(isManual) {
            if (isManual) {
                this.$v.form.target.$model = null
            }
        },
    },
}
</script>
